/* --------------------------------------------------
                Imports
-------------------------------------------------- */
import { Controller } from "@hotwired/stimulus"
import Choices from "choices.js"

/* --------------------------------------------------
                Choices Table Sync Controller
-------------------------------------------------- */
export default class extends Controller {
  static targets = ["select", "table", "tableBody", "richTemplate", "simpleTemplate"]
  static values = {
    removeItemButton: { type: Boolean, default: true },
    searchEnabled: { type: Boolean, default: true },
    placeholder: { type: Boolean, default: true },
    itemSelectText: String,
    route: String,
    planroomId: String,
    role: String
  }

  connect() {
    this.removingFromTable = false // Flag to prevent double removal
    this.debounceTimeout = null
    this.initializeChoices()
    this.setupEventListeners()
    this.setupDynamicLoading()
  }

  disconnect() {
    if (this.choices) {
      this.choices.destroy()
    }
  }

  initializeChoices() {
    // Before initializing Choices.js, store custom properties for pre-selected items
    // by reading from the table rows (which have the rich data)
    this.preSelectedData = new Map()

    if (this.hasRouteValue) {
      // For dynamic loading contexts, extract data from existing table rows using data attributes
      const existingRows = this.tableBodyTarget.querySelectorAll('tr[data-item-id]:not([data-item-id="-1"])')
      existingRows.forEach(row => {
        const itemId = row.getAttribute('data-item-id')
        const nameEl = row.querySelector('[data-user-name]')
        const emailEl = row.querySelector('[data-user-email]')
        const companyEl = row.querySelector('[data-user-company]')

        if (nameEl && emailEl && companyEl) {
          this.preSelectedData.set(itemId, {
            name: nameEl.getAttribute('data-user-name'),
            email: emailEl.getAttribute('data-user-email'),
            company: companyEl.getAttribute('data-user-company')
          })
        }
      })
    }

    // Initialize Choices.js with config to hide selected items in the input
    this.choices = new Choices(this.selectTarget, {
      closeDropdownOnSelect: 'true',
      removeItemButton: false,
      removeItems: true,
      searchEnabled: this.searchEnabledValue,
      placeholder: this.placeholderValue,
      itemSelectText: this.itemSelectTextValue || ''
    })
  }

  setupEventListeners() {
    // Listen for when an item is added
    this.selectTarget.addEventListener('addItem', (event) => {
      this.addRowToTable(event.detail)
    })

    // Listen for when an item is removed via Choices.js
    this.selectTarget.addEventListener('removeItem', (event) => {
      // Only remove from table if we didn't trigger this removal
      if (!this.removingFromTable) {
        this.removeRowFromTable(event.detail.value)
      }
    })
  }

  addRowToTable(detail) {
    const { value, label, customProperties } = detail

    // Check if row already exists
    if (this.tableBodyTarget.querySelector(`tr[data-item-id="${value}"]`)) {
      return
    }

    // Choose the appropriate template based on what's available
    // If we have a route (dynamic loading), use rich template
    // Otherwise use simple template
    let template
    let useRichTemplate = false

    if (this.hasRouteValue) {
      // Dynamic loading context - always use rich template
      template = this.hasRichTemplateTarget ? this.richTemplateTarget : null
      useRichTemplate = true
    } else {
      // Static context - use simple template
      template = this.hasSimpleTemplateTarget ? this.simpleTemplateTarget : null
    }

    if (!template) {
      console.error('No template available for this data type')
      return
    }

    // Clone the template content
    const row = template.content.cloneNode(true).querySelector('tr')

    // Set the item ID
    row.setAttribute('data-item-id', value)

    // Set the remove button value
    row.querySelector('[data-action*="removeItem"]').setAttribute('data-value', value)

    // Populate the fields based on template type
    if (useRichTemplate) {
      // Rich template - populate all fields
      // Check if we have customProperties from dynamic load
      let data = customProperties && customProperties.name ? customProperties : null

      // If not, check if we have pre-selected data
      if (!data && this.preSelectedData.has(value.toString())) {
        data = this.preSelectedData.get(value.toString())
      }

      if (data) {
        row.querySelector('[data-field="name"]').textContent = data.name
        row.querySelector('[data-field="company"]').textContent = data.company
        row.querySelector('[data-field="email"]').textContent = data.email
      } else {
        // Fallback: use label as name
        row.querySelector('[data-field="name"]').textContent = label
      }
    } else {
      // Simple template - just set name
      row.querySelector('[data-field="name"]').textContent = label
    }

    this.tableBodyTarget.prepend(row)

    // Hide empty state after adding the row
    this.toggleEmptyState()
  }

  removeRowFromTable(value) {
    const row = this.tableBodyTarget.querySelector(`tr[data-item-id="${value}"]`)
    if (row) {
      row.remove()
      // Show empty state if no items left
      this.toggleEmptyState()
    }
  }

  toggleEmptyState() {
    const itemCount = this.tableBodyTarget.querySelectorAll('tr[data-item-id]:not([data-item-id="-1"])').length
    if (itemCount === 0) {
      this.tableTarget.classList.add('hidden')
    } else {
      this.tableTarget.classList.remove('hidden')
    }
  }

  removeItem(event) {
    event.preventDefault()
    const value = event.currentTarget.dataset.value

    // Set flag to prevent the removeItem event from also removing the row
    this.removingFromTable = true

    this.choices.removeActiveItemsByValue(value)
    this.choices.removeActiveItemsByValue(Number(value))

    // Remove from table
    this.removeRowFromTable(value)

    // Reset flag
    this.removingFromTable = false
  }

  setupDynamicLoading() {
    // Check if dynamic loading is enabled (if route is provided)
    if (!this.hasRouteValue) {
      return
    }

    // Set up keyup listener for dynamic loading
    this.choices.input.element.addEventListener('keyup', (event) => {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => {
        this.loadData(event.target.value)
      }, 300)
    })

    // Load initial data
    this.loadData('')
  }

  loadData(searchValue) {
    const planroomId = this.hasPlanroomIdValue ? this.planroomIdValue : ''
    const role = this.hasRoleValue ? this.roleValue : ''
    const url = `/api/v1/${this.routeValue}?search=${searchValue}&planroom_id=${planroomId}&role=${role}`

    fetch(url)
      .then(response => response.json())
      .then(data => {
        // Get currently selected values
        const selectedValues = this.choices.getValue(true)

        // Filter out already selected items from the choices
        const availableChoices = data.filter(item => {
          const itemId = Number(item.id)
          const isSelected = selectedValues.some(v => Number(v) === itemId)
          return !isSelected
        })

        // Clear only the dropdown choices (not selected items)
        this.choices.clearChoices()

        // Transform data to include custom properties that Choices.js will pass through
        const choicesData = availableChoices.map(item => ({
          value: item.id,
          label: item.display_name,
          customProperties: {
            name: item.name,
            email: item.email,
            company: item.company
          }
        }))

        // Add back the available choices
        this.choices.setChoices(choicesData, 'value', 'label', false)
      })
      .catch(error => {
        console.error('Failed to load results:', error)
      })
  }

}